import { GROUP_TOPIC_TYPES } from 'util/constants'

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useContext } from 'react'

import { useFullscreen } from '../../../hooks/use_fullscreen'
import {
  AppLocationContext,
  BLANK_APP_LOCATION,
  MENU_TAB,
  TOPIC_VIEW,
  WIDGET_VIEW,
} from '../../../pages/admin/app_location_provider'
import { isLiveChatApp } from '../../../reduxes/slices/live_chat_app/selectors'
import { getHandlesIndexedByMemberId } from '../../../reduxes/slices/members/selectors'
import { getRoutesWithoutPrefill } from '../../../reduxes/slices/routes/selectors'
import { useSelectorDelegateStore } from '../../../reduxes/stores/root_store/hooks'

export const useCallNavHandler = (
  activeMemberId: number | null,
  activeTopic: any,
) => {
  const { setAppLocation, setAppLocationCache } = useContext(AppLocationContext)
  const activeTopicId = activeTopic?.id
  const routesWithoutPrefill: any = useSelectorDelegateStore(
    getRoutesWithoutPrefill,
  )
  const handleLookup = useSelectorDelegateStore(getHandlesIndexedByMemberId)
  const activeHandle = activeMemberId && handleLookup[activeMemberId]
  const isGroupChat =
    !!activeTopic && GROUP_TOPIC_TYPES.includes(activeTopic.participant_mode)
  const directChatHandle =
    activeTopic &&
    activeHandle &&
    !isGroupChat &&
    activeTopic.contact_handles.find((h: string) => h !== activeHandle)

  const callPath =
    activeTopic &&
    (isGroupChat
      ? routesWithoutPrefill.admin_app_group_chats_selected.path
      : routesWithoutPrefill.admin_app_direct_chats_selected.path)
  const topicsPath = routesWithoutPrefill.admin_app_group_chats_selected.path
  const directChatsPath =
    routesWithoutPrefill.admin_app_direct_chats_selected.path
  const { exitFullscreen: exitFullscreenFn } = useFullscreen()
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  const navToVideoView = useCallback(() => {
    if (isGroupChat) {
      setAppLocation({
        ...BLANK_APP_LOCATION,
        memberId: activeMemberId,
        topicId: activeTopicId,
        topicView: TOPIC_VIEW.VIDEO_VIEW,
        menuTab: MENU_TAB.LATEST,
        url: callPath
          .replace(':current_handle', activeHandle)
          .replace(':id', activeTopicId),
        ...(liveChatApp && { widgetView: WIDGET_VIEW.MESSENGER }),
      })
    } else {
      setAppLocation({
        ...BLANK_APP_LOCATION,
        memberId: activeMemberId,
        topicId: activeTopicId,
        directChatHandle: directChatHandle,
        topicView: TOPIC_VIEW.VIDEO_VIEW,
        menuTab: MENU_TAB.LATEST,
        url: callPath
          .replace(':current_handle', activeHandle)
          .replace(':handle', directChatHandle),
        ...(liveChatApp && { widgetView: WIDGET_VIEW.MESSENGER }),
      })
    }
  }, [
    activeHandle,
    activeMemberId,
    activeTopicId,
    callPath,
    directChatHandle,
    isGroupChat,
    liveChatApp,
    setAppLocation,
  ])

  const navToChatView: NavToChatViewType = useCallback(
    (options) => {
      const { exitFullscreen } = {
        exitFullscreen: true,
        ...options,
      }
      if (exitFullscreen) {
        exitFullscreenFn()
      }
      if (isGroupChat) {
        setAppLocation({
          ...BLANK_APP_LOCATION,
          memberId: activeMemberId,
          topicId: activeTopicId,
          topicView: TOPIC_VIEW.CHAT_VIEW,
          menuTab: MENU_TAB.LATEST,
          url: topicsPath
            .replace(':current_handle', activeHandle)
            .replace(':id', activeTopicId),
          ...(liveChatApp && { widgetView: WIDGET_VIEW.MESSENGER }),
        })
      } else {
        setAppLocation({
          ...BLANK_APP_LOCATION,
          memberId: activeMemberId,
          topicId: activeTopicId,
          directChatHandle: directChatHandle,
          topicView: TOPIC_VIEW.CHAT_VIEW,
          menuTab: MENU_TAB.LATEST,
          url: directChatsPath
            .replace(':current_handle', activeHandle)
            .replace(':handle', directChatHandle),
          ...(liveChatApp && { widgetView: WIDGET_VIEW.MESSENGER }),
        })
      }
    },
    [
      activeHandle,
      activeMemberId,
      activeTopicId,
      directChatHandle,
      directChatsPath,
      exitFullscreenFn,
      isGroupChat,
      liveChatApp,
      setAppLocation,
      topicsPath,
    ],
  )

  const setMemberCacheToChatView = useCallback(() => {
    if (isGroupChat) {
      setAppLocationCache({
        memberId: activeMemberId,
        topicId: activeTopicId,
        topicView: TOPIC_VIEW.CHAT_VIEW,
        menuTab: MENU_TAB.LATEST,
      })
    } else {
      setAppLocationCache({
        memberId: activeMemberId,
        topicId: activeTopicId,
        directChatHandle: directChatHandle,
        topicView: TOPIC_VIEW.CHAT_VIEW,
        menuTab: MENU_TAB.LATEST,
      })
    }
  }, [
    activeMemberId,
    activeTopicId,
    directChatHandle,
    isGroupChat,
    setAppLocationCache,
  ])

  return {
    navToVideoView,
    navToChatView,
    setMemberCacheToChatView,
  }
}

export type NavToChatViewType = (arg0?: { exitFullscreen: boolean }) => void
